import {
  createBrowserRouter,
  Navigate,
  Route,
  RouterProvider,
  Routes,
  useLocation,
} from 'react-router-dom';
import {AccessRestrictionModalSystem} from '@/components/AccessRestrictionModalSystem';
import {CouponLoginAlertSystem} from '@/components/Coupon';
import {SessionErrorModalSystem} from '@/components/SessionErrorModalSystem';
import {Splash} from '@/components/Splash';
import {useHistoryStack} from '@/hooks';
import {useSession, useSessionObserver} from '@/hooks/session';
import ErrorBoundary from '@/pages/ErrorBoundary';
import Intro from '@/pages/Intro';
import LGUPlusMap from '@/pages/LGUPlus';
import Map from '@/pages/Map';
import MyAccount from '@/pages/MyAccount';
import PayInfo from '@/pages/PayInfo';
import PayProcess from '@/pages/PayProcess';
import Pricing from '@/pages/Pricing';
import SignIn from '@/pages/SignIn';
import Welcome from '@/pages/Welcome';
import LoginRoute from '@/router/LoginRoute';
import {useSetScreenSize} from '@/styles/hooks';
import AccessRestrictionRoute from './AccessRestrictionRoute';
import {LG_UPLUS_PATH, paths} from './path';
import ProtectedRoute from './ProtectedRoute';

const Root = () => {
  useHistoryStack();
  useSetScreenSize();
  const {pathname} = useLocation();

  return (
    <ErrorBoundary>
      <SessionErrorModalSystem />
      <AccessRestrictionModalSystem />
      {![LG_UPLUS_PATH, paths.payments_root].some((p) =>
        pathname.startsWith(p),
      ) && <CouponLoginAlertSystem />}
      <Routes>
        <Route
          path={paths.signin}
          element={
            <LoginRoute>
              <SignIn />
            </LoginRoute>
          }
        />
        <Route path={paths.intro} element={<Intro />} />
        <Route path={paths.pricing_root} element={<Pricing />} />
        <Route
          path={paths.mypage_account}
          element={
            <ProtectedRoute>
              <MyAccount />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.mypage_pay}
          element={
            <ProtectedRoute>
              <PayInfo />
            </ProtectedRoute>
          }
        />
        <Route
          path={paths.payments_process}
          element={
            <ProtectedRoute>
              <PayProcess />
            </ProtectedRoute>
          }
        />
        <Route path={paths.welcome} element={<Welcome />} />
        <Route
          path={paths.map_segment}
          element={
            <ProtectedRoute>
              <AccessRestrictionRoute>
                <Map />
              </AccessRestrictionRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={LG_UPLUS_PATH}
          element={
            <ProtectedRoute>
              <LGUPlusMap />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFoundRouter />} />
      </Routes>
    </ErrorBoundary>
  );
};

const NotFoundRouter = () => {
  const location = useLocation();
  const {isPaidUser} = useSession();

  return (
    <Navigate
      to={{
        pathname: isPaidUser ? paths.map_root : paths.intro,
        search: location.search,
      }}
      replace
    />
  );
};

const router = createBrowserRouter([
  {
    path: '*',
    element: <Root />,
  },
]);

const Router = () => {
  const {isFetched} = useSessionObserver();

  if (!isFetched) {
    return <Splash />;
  }

  return <RouterProvider router={router} />;
};

export default Router;
